import React from 'react';
import {
  BookOpen, ClipboardList, Table, MoreVertical, Pencil, FolderInput, Trash2, Notebook, Plus,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

const TYPE_META = {
  lesson_plan: { label: 'Lesson plan', icon: BookOpen, tone: 'bg-[#FBF6E8] text-[#8A6D1B] border-[#D4AF37]/30' },
  skeleton_notes: { label: 'Skeleton notes', icon: ClipboardList, tone: 'bg-[#EDF3F7] text-[#2C4A6B] border-[#7A93AC]/30' },
  rubric: { label: 'Rubric', icon: Table, tone: 'bg-[#F3EEE7] text-[#7A5A2E] border-[#B79877]/30' },
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
};

const LessonCard = ({ lesson, folder, onOpen, onMove, onDelete, onRename }) => {
  const meta = TYPE_META[lesson.artifact_type] || TYPE_META.lesson_plan;
  const Icon = meta.icon;

  return (
    <article
      className="group relative bg-white border border-[#EBE7E0] rounded-lg p-4 hover:shadow-md hover:border-[#D4AF37]/40 transition-all cursor-pointer flex flex-col"
      onClick={() => onOpen(lesson)}
      data-testid={`lesson-card-${lesson.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${meta.tone}`}>
          <Icon className="h-3 w-3" strokeWidth={1.75} />
          {meta.label}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              className="text-[#5A6577] hover:text-[#14213D] hover:bg-[#F9F8F4] rounded p-1 -mr-1 -mt-1"
              data-testid={`lesson-menu-${lesson.id}`}
              aria-label="Actions"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={(e) => {
              e.stopPropagation();
              const t = window.prompt('New title', lesson.title);
              if (t !== null) onRename(lesson, t.trim());
            }} data-testid={`lesson-rename-${lesson.id}`}>
              <Pencil className="h-3.5 w-3.5 mr-2" /> Rename
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMove(lesson); }} data-testid={`lesson-move-${lesson.id}`}>
              <FolderInput className="h-3.5 w-3.5 mr-2" /> Move to folder…
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => { e.stopPropagation(); onDelete(lesson); }}
              className="text-red-600 focus:text-red-700 focus:bg-red-50"
              data-testid={`lesson-delete-${lesson.id}`}
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <h3 className="font-serif-display text-base sm:text-lg text-[#14213D] leading-tight line-clamp-2 mb-1">
        {lesson.title}
      </h3>

      {lesson.description ? (
        <p className="text-xs text-[#5A6577] line-clamp-2 mb-3 leading-relaxed">{lesson.description}</p>
      ) : lesson.unit_title ? (
        <p className="text-xs text-[#5A6577] mb-3 italic line-clamp-1">{lesson.unit_title}</p>
      ) : null}

      <div className="mt-auto flex items-center justify-between gap-2 text-[11px] text-[#5A6577] pt-2 border-t border-[#F0EBE1]">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          {lesson.grade_level && (
            <span className="bg-[#F9F8F4] px-1.5 py-0.5 rounded uppercase tracking-wider">Grade {lesson.grade_level}</span>
          )}
          {folder && (
            <span className="flex items-center gap-1 truncate">
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: folder.color || '#D4AF37' }} />
              <span className="truncate">{folder.name}</span>
            </span>
          )}
        </div>
        <span className="tabular-nums flex-shrink-0">{formatDate(lesson.updated_at)}</span>
      </div>
    </article>
  );
};

const EmptyState = ({ hasFolder, filterApplied, onGenerateNew }) => {
  let title = 'Your library is empty';
  let body = 'Save AI-generated lesson plans, skeleton notes, and rubrics so you can revisit and reuse them later.';
  if (filterApplied) {
    title = 'No matches';
    body = 'Try clearing your filters or search to see more items.';
  } else if (hasFolder) {
    title = 'Nothing here yet';
    body = 'This folder is empty. Generate a new lesson or move an existing one into it.';
  }
  return (
    <div className="bg-white border border-dashed border-[#EBE7E0] rounded-lg p-10 text-center" data-testid="library-empty-state">
      <div className="w-12 h-12 mx-auto rounded-full bg-[#F9F8F4] border border-[#EBE7E0] flex items-center justify-center mb-4">
        <Notebook className="h-5 w-5 text-[#5A6577]" strokeWidth={1.5} />
      </div>
      <h3 className="font-serif-display text-lg text-[#14213D] mb-1">{title}</h3>
      <p className="text-sm text-[#5A6577] max-w-md mx-auto mb-6">{body}</p>
      {!filterApplied && (
        <Button
          onClick={onGenerateNew}
          className="bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7]"
          size="sm"
          data-testid="empty-generate-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Generate a lesson
        </Button>
      )}
    </div>
  );
};

export default function LessonGrid({
  lessons, folders, onOpen, onMove, onDelete, onRename, onGenerateNew, emptyContext,
}) {
  if (!lessons.length) {
    return <EmptyState {...emptyContext} onGenerateNew={onGenerateNew} />;
  }

  const folderMap = new Map(folders.map((f) => [f.id, f]));

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4" data-testid="lesson-grid">
      {lessons.map((lesson) => (
        <LessonCard
          key={lesson.id}
          lesson={lesson}
          folder={lesson.folder_id ? folderMap.get(lesson.folder_id) : null}
          onOpen={onOpen}
          onMove={onMove}
          onDelete={onDelete}
          onRename={onRename}
        />
      ))}
    </div>
  );
}
